<template>
  <Component
    ref="dialog"
    :is="hasOnlyOneBuilding ? 'div' : maxSmall ? Drawer : Collapsible"
    variant="nav"
    v-bind="maxSmall ? { size: 'full', showHeader: !searchingOnMobile } : {}"
    class="flex flex-col transition duration-400 ease-out data-[state=open]:md:grow w-fit md:w-full group-data-[collapsed=true]/nav:md:shadow-none group-data-[collapsed=true]/nav:md:bg-transparent group-data-[collapsed=true]/nav:md:hover:bg-transparent"
    :class="[
      open ? '' : 'md:border-b border-alpha-white-20',
      {
        'border-alpha-white-20': !hasOnlyOneBuilding,
      },
    ]"
    background="white"
    tracking-id="building-selector"
    v-model:open="open"
    @update:open="searchingOnMobile = false"
  >
    <template #default>
      <BuildingSelectorTrigger
        v-model:open="open"
        :organisation="organisation"
        :selected="selectedOption"
        :disabled="hasOnlyOneBuilding"
      />
    </template>
    <template v-if="maxSmall" #header>
      <DrawerHeader
        ref="header"
        :title="$t('components.buttons.make_selection')"
        :leadingAction="
          !searchingOnMobile && {
            icon: 'search',
            label: $t('components.buttons.search'),
            iconOnly: true,
            event: 'search',
          }
        "
        @search="searchingOnMobile = true"
        :class="{
          'border-b': !searchingOnMobile,
        }"
      />
    </template>
    <template #content>
      <div
        class="flex flex-col md:px-2 pb-4 md:pb-2 gap-3 grow"
        :class="{ 'px-4': !searchingOnMobile }"
      >
        <SelectRoot :modelValue="selected" @update:modelValue="updateSelected">
          <Component
            :is="maxSmall ? 'div' : NavDivider"
            class="flex flex-col"
            :class="{ 'pt-2 gap-2': !searchingOnMobile }"
          >
            <div
              v-if="searchingOnMobile || !maxSmall"
              class="flex flex-col pb-3 md:py-2 px-4 md:px-2"
            >
              <BuildingSelectorSearch
                v-model:query="query"
                @close="searchingOnMobile = false"
              />
            </div>
            <div class="flex flex-col md:px-1.5 md:gap-1">
              <div class="flex flex-col">
                <BuildingSelectorItem
                  v-if="!query && !searchingOnMobile"
                  :item="allBuildings"
                  :active="allBuildings.id === selected"
                  :variant="variant"
                  :isGroup="true"
                />
              </div>
              <div
                class="flex flex-col md:gap-1"
                :class="{
                  '[&>div:first-of-type]:border-t-0': searchingOnMobile,
                }"
              >
                <div
                  v-for="building in filteredBuildings"
                  :key="building.id"
                  :class="{
                    'py-2 px-4 border-t border-primary': searchingOnMobile,
                  }"
                >
                  <BuildingSelectorItem
                    :item="building"
                    :active="building.id === selected"
                    :variant="variant"
                  >
                    <span
                      v-html="
                        highlight(
                          building.name,
                          query,
                          'fg-primary md:text-alpha-white-100 font-semibold -tracking-[0.018em]'
                        )
                      "
                    />
                  </BuildingSelectorItem>
                </div>
              </div>
            </div>
          </Component>
        </SelectRoot>
      </div>
    </template>
  </Component>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { SelectRoot } from "radix-vue";
import { Collapsible } from "@/SharedComponents/Collapsible";
import { Drawer, DrawerHeader } from "@/SharedComponents/Drawer";
import {
  BuildingSelectorItem,
  BuildingSelectorSearch,
  BuildingSelectorTrigger,
  NavDivider,
} from "@/SharedComponents/Navigation";
import { FilterType } from "@/Components/Filters";
import { useFilters } from "@/Composables/useFilters";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useClickOutside } from "@/Composables/useClickOutside";
import { highlight } from "@/Utils/highlight";
import { useI18n } from "vue-i18n";
import { QUERY_KEY as TASK_VIEWS_QUERY_KEY } from "@/Composables/useTasksViews";
import { QUERY_KEY as FILE_VIEWS_QUERY_KEY } from "@/Composables/useFilesViews";
import { useQueryClient } from "@tanstack/vue-query";

const { maxSmall } = useBreakpoints();
const { t } = useI18n();

const open = ref(undefined);

const dialog = ref(null);
const header = ref(null);

const queryClient = useQueryClient();

const props = defineProps({
  organisation: { type: String, required: true },
  buildings: { type: Array, default: () => [] },
  variant: { type: String, default: "on-brand" },
});

const hasOnlyOneBuilding = computed(() => props.buildings.length === 1);

const allBuildings = ref({
  id: "all",
  name: t("admin.navigation.building_selector.all"),
});

const searchingOnMobile = ref(false);
const query = ref("");

const filteredBuildings = computed(() => {
  if (!query.value) {
    return props.buildings;
  }
  return props.buildings.filter((building) =>
    building.name.toLowerCase().includes(query.value.toLowerCase())
  );
});

const filterStore = useFilters("global");
const { filterStates } = storeToRefs(filterStore);
const { handleChangeFilter, handleClearFilters } = filterStore;

const selected = computed(() => {
  let id = filterStates.value[FilterType.LINKED_TO]?.buildings?.[0] ?? "all";
  if (hasOnlyOneBuilding.value) {
    id = props.buildings[0].id;
  }
  return options.value.find((option) => option.id === id) ? id : "all";
});

const options = computed(() => {
  return [allBuildings.value, ...props.buildings];
});

const selectedOption = computed(() => {
  return options.value.find((option) => option.id === selected.value);
});

watch(
  () => open.value,
  (bool) => {
    if (bool) query.value = "";
  }
);

function collapse() {
  if (maxSmall.value && !searchingOnMobile.value) {
    header.value?.close();
  } else {
    open.value = false;
  }
  searchingOnMobile.value = false;
}

function updateSelected(value) {
  if (value === "all") {
    handleClearFilters([FilterType.LINKED_TO]);
  } else {
    handleChangeFilter(FilterType.LINKED_TO, {
      buildings: [value],
    });
  }
  collapse();

  queryClient.resetQueries({ queryKey: TASK_VIEWS_QUERY_KEY });
  queryClient.resetQueries({ queryKey: FILE_VIEWS_QUERY_KEY });
}

useClickOutside(dialog, collapse, { handleOnMobile: false });

defineExpose({ open });
</script>
